import type * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';
import { ChatMarkdown } from './chat-markdown.tsx';
import { ChatSuggestion } from './chat-suggestions.tsx';
import { sameOriginPath } from './sameOriginPath.ts';

export interface ChatCardSection {
  label: string;
  content: string;
}

export interface ChatCardButton {
  id: string;
  title: string;
  /** When set the button opens this address; otherwise it says its title. */
  url?: string;
}

export interface ChatCardProps extends Omit<
  React.ComponentProps<'div'>,
  'style' | 'children'
> {
  header?: string;
  body?: string;
  footer?: string;
  sections?: ChatCardSection[];
  buttons?: ChatCardButton[];
  /** What a button that is not a link does: say its title, as the user. */
  onAction?: (title: string) => void;
  /**
   * Whether there is still anything to answer here. A card the conversation
   * has moved past keeps its buttons on show, greyed, so what was offered can
   * still be read. Links are left alone: following one says nothing.
   */
  disabled?: boolean;
}

/**
 * A message with more in it than a sentence: a heading, a few labelled parts,
 * and something to do about them. Wundo sends these on every channel — a
 * drafted email, a meeting to confirm — so the drawer has to show them too.
 *
 * The heading is a heading rather than bold text, because someone moving
 * through the conversation by structure should find it.
 */
export function ChatCard({
  header,
  body,
  footer,
  sections,
  buttons,
  onAction,
  disabled = false,
  className,
  ...props
}: ChatCardProps) {
  return (
    <div data-slot='chat-card' className={cn('flex flex-col', className)} {...props}>
      {header ? <h3 className='text-md font-bold'>{header}</h3> : null}

      {body ? <ChatMarkdown className='mt-1'>{body}</ChatMarkdown> : null}

      {sections?.map((section) => (
        <div key={section.label} className='mt-2.5'>
          <p className='text-xs font-bold tracking-wide uppercase'>{section.label}</p>
          <ChatMarkdown className='mt-0.5'>{section.content}</ChatMarkdown>
        </div>
      ))}

      {footer ? <p className='mt-2 text-xs text-ink-muted'>{footer}</p> : null}

      {buttons?.length ? (
        <div className='mt-3 flex flex-col gap-1.5'>
          {buttons.map((button) =>
            button.url ? (
              <ChatCardLink key={button.id} href={button.url}>
                {button.title}
              </ChatCardLink>
            ) : (
              <ChatSuggestion
                key={button.id}
                text={button.title}
                disabled={disabled}
                onClick={() => onAction?.(button.title)}
              />
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}

export type ChatCardLinkProps = Omit<
  React.ComponentProps<'a'>,
  'style' | 'target' | 'rel'
>;

/*
 * The look of a link inside a card, on its own as well. Exported as variants
 * too, because on a routed page the element is the consumer's — a router Link
 * that stays inside the app — the same arrangement as backLinkVariants.
 */
export const chatCardLinkVariants = cva([
  'text-base font-semibold underline underline-offset-2',
]);

/**
 * A link out of the conversation.
 *
 * Somewhere else entirely gets a tab of its own: leaving the page would take
 * the chat with it, and the reply the user is reading along with it. One of our
 * own pages does not — the user stays in the app either way, and the drawer
 * comes along.
 */
export function ChatCardLink({ className, href, ...props }: ChatCardLinkProps) {
  const leaves = sameOriginPath(href) === null;

  return (
    <a
      data-slot='chat-card-link'
      href={href}
      target={leaves ? '_blank' : undefined}
      rel={leaves ? 'noopener noreferrer' : undefined}
      className={cn(chatCardLinkVariants(), className)}
      {...props}
    />
  );
}
